// pages/Login.jsx
import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { loginUser } from '../utils/Axios';
import './css/Login.css';
import CountryCodeSelector from '../utils/CountryCodeSelector';

const Login = () => {
  const navigate = useNavigate();
  const [loginMethod, setLoginMethod] = useState('phone');
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    countryCode: '+91',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isVisible, setIsVisible] = useState(false);

  React.useEffect(() => {
    setIsVisible(true);
  }, []);

  // Check if user is already logged in - using Navigate component
  const token = sessionStorage.getItem('kt-auth-react-v');
  if (token) {
    return <Navigate to="/" />;
  }

  const validateForm = () => {
    const newErrors = {};

    if (loginMethod === 'email') {
      if (!formData.email || !formData.email.trim()) {
        newErrors.email = 'Email or Username is required';
      }
    } else {
      if (!formData.phone || !formData.phone.trim()) {
        newErrors.phone = 'Phone number or Username is required';
      }
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    setLoginError('');
  };

  const handleCountryCodeChange = (code) => {
    setFormData(prev => ({ ...prev, countryCode: code }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setLoginError('');

    try {
      const formattedPhone = formData.phone.startsWith('+') || isNaN(formData.phone.replace(/[\s-]/g, ''))
        ? formData.phone
        : (formData.countryCode + formData.phone);

      const loginData = {
        mobile: loginMethod === 'phone' ? formattedPhone : '',
        password: formData.password,
        email: loginMethod === 'email' ? formData.email : '',
        loginWith: loginMethod
      };

      const response = await loginUser(loginData);

      if (response && response.data && response.data.token) {
        // Store token
        sessionStorage.setItem('kt-auth-react-v', response.data.token);

        // Store user data if needed
        if (response.data) {
          const user = {
            id: response.data.id,
            username: response.data.username
          };
          sessionStorage.setItem('kt-user-data', JSON.stringify(user));
        }

        // Notify App (same tab) that auth state changed.
        // 'storage' events don't fire in the tab that made the change,
        // so without this App's isAuthenticated stays false and the
        // router bounces between "/" and "/login" forever.
        window.dispatchEvent(new Event('kt-auth-changed'));

        // Use navigate with replace to prevent back button issues
        setIsLoading(false);
        navigate('/', { replace: true });
      } else {
        setLoginError('Invalid login response. Please try again.');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error.response && error.response.data && error.response.data.message) {
        setLoginError(error.response.data.message);
      } else if (error.response && error.response.status === 401) {
        setLoginError('Invalid email/phone or password. Please try again.');
      } else if (error.response && error.response.status === 404) {
        setLoginError('Account not found. Please check your credentials.');
      } else {
        setLoginError('Login failed. Please check your connection and try again.');
      }
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    alert('Password reset functionality coming soon!');
  };

  return (
    <div className="irp-login-container">
      {/* Animated background particles */}
      <div className="irp-login-particles">
        <div className="irp-login-particle"></div>
        <div className="irp-login-particle"></div>
        <div className="irp-login-particle"></div>
        <div className="irp-login-particle"></div>
        <div className="irp-login-particle"></div>
      </div>

      <div className={`irp-login-wrapper ${isVisible ? 'irp-login-visible' : ''}`}>
        <div className="irp-login-left">
          <div className="irp-login-brand">
            <div className="irp-login-logo">
              <div className="irp-login-logo-icon">
                <img width='48' height="48" src="/favicon.ico" alt="" />
              </div>
              <span className="irp-login-brand-name">IXES <span className="irp-login-brand-highlight">IRP</span></span>
            </div>
            <h1 className="irp-login-title">Welcome Back</h1>
            <p className="irp-login-subtitle">Sign in to access your Integrated Resource Platform</p>
          </div>

          <form className="irp-login-form" onSubmit={handleSubmit}>
            {loginError && (
              <div className="irp-login-error-banner">
                <span className="irp-login-error-icon">⚠️</span>
                <span>{loginError}</span>
                <button
                  type="button"
                  className="irp-login-error-close"
                  onClick={() => setLoginError('')}
                >
                  ×
                </button>
              </div>
            )}

            <div className="irp-login-method-toggle">
              <button
                type="button"
                className={`irp-login-method-btn ${loginMethod === 'email' ? 'irp-login-method-active' : ''}`}
                onClick={() => setLoginMethod('email')}
                disabled={isLoading}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2 4a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V4zm2 0v12h12V4H4z"/>
                  <path d="M10 11l-4-4H6l4 4 4-4h-2l-4 4z"/>
                </svg>
                Email
              </button>
              <button
                type="button"
                className={`irp-login-method-btn ${loginMethod === 'phone' ? 'irp-login-method-active' : ''}`}
                onClick={() => setLoginMethod('phone')}
                disabled={isLoading}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z"/>
                </svg>
                Phone
              </button>
            </div>

            {loginMethod === 'email' ? (
              <div className="irp-login-field irp-login-field-email">
                <label className="irp-login-label">
                  Email Address
                  <span className="irp-login-required">*</span>
                </label>
                <div className="irp-login-input-wrapper">
                  <svg className="irp-login-input-icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                  </svg>
                  <input
                    type="email"
                    name="email"
                    className={`irp-login-input ${errors.email ? 'irp-login-input-error' : ''}`}
                    placeholder="Enter your email address"
                    value={formData.email}
                    onChange={handleInputChange}
                    disabled={isLoading}
                    autoFocus
                  />
                  {formData.email && !errors.email && (
                    <div className="irp-login-input-valid">✓</div>
                  )}
                </div>
                {errors.email && <span className="irp-login-error">{errors.email}</span>}
              </div>
            ) : (
              <div className="irp-login-field irp-login-field-phone">
                <label className="irp-login-label">
                  Phone Number
                  <span className="irp-login-required">*</span>
                </label>
                <div className="irp-login-phone-wrapper">
                  <CountryCodeSelector
                    value={formData.countryCode}
                    onChange={handleCountryCodeChange}
                    disabled={isLoading}
                  />
                  <div className="irp-login-input-wrapper irp-login-phone-input-wrapper">
                    <input
                      type="tel"
                      name="phone"
                      className={`irp-login-input irp-login-phone-input ${errors.phone ? 'irp-login-input-error' : ''}`}
                      placeholder="Enter phone number"
                      value={formData.phone}
                      onChange={handleInputChange}
                      disabled={isLoading}
                      maxLength="10"
                    />
                    {formData.phone && !errors.phone && (
                      <div className="irp-login-input-valid">✓</div>
                    )}
                  </div>
                </div>
                {errors.phone && <span className="irp-login-error">{errors.phone}</span>}
              </div>
            )}

            <div className="irp-login-field irp-login-field-password">
              <label className="irp-login-label">
                Password
                <span className="irp-login-required">*</span>
              </label>
              <div className="irp-login-input-wrapper">
                <svg className="irp-login-input-icon" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2V7a5 5 0 00-5-5zm0 10a1 1 0 011 1v1a1 1 0 01-2 0v-1a1 1 0 011-1z"/>
                </svg>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  className={`irp-login-input ${errors.password ? 'irp-login-input-error' : ''}`}
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  className="irp-login-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={isLoading}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"/>
                      <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"/>
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd"/>
                      <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z"/>
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && <span className="irp-login-error">{errors.password}</span>}
            </div>

            <div className="irp-login-options">
              <label className="irp-login-remember">
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleInputChange}
                  disabled={isLoading}
                />
                <span className="irp-login-remember-text">Remember me</span>
              </label>
              <button
                type="button"
                className="irp-login-forgot"
                onClick={handleForgotPassword}
                disabled={isLoading}
              >
                Forgot password?
              </button>
            </div>

            <button
              type="submit"
              className={`irp-login-submit ${isLoading ? 'irp-login-submit-loading' : ''}`}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="irp-login-spinner"></span>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 010-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd"/>
                  </svg>
                </>
              )}
            </button>

            <div className="irp-login-divider">
              <span className="irp-login-divider-text">Secure login</span>
            </div>
          </form>

          <div className="irp-login-footer">
            <p className="irp-login-footer-text">
              &copy; {new Date().getFullYear()} IXES IRP. All rights reserved.
            </p>
          </div>
        </div>

        <div className="irp-login-right">
          <div className="irp-login-hero">
            <div className="irp-login-hero-content">
              <div className="irp-login-hero-icon">
                <img width="180" height="180" src='/IxesSimpleXwhite.png' />
              </div>
              <h2>Integrated Resource Platform</h2>
              <p>Manage your entire business ecosystem from a single, unified dashboard.</p>
              <ul className="irp-login-features">
                <li>
                  <span className="irp-login-feature-icon">✓</span>
                  <span>Comprehensive CRM</span>
                </li>
                <li>
                  <span className="irp-login-feature-icon">✓</span>
                  <span>Project Management</span>
                </li>
                <li>
                  <span className="irp-login-feature-icon">✓</span>
                  <span>Inventory &amp; Finance</span>
                </li>
                <li>
                  <span className="irp-login-feature-icon">✓</span>
                  <span>HR &amp; Reports</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;